"use client";

import * as stylex from "@stylexjs/stylex";
import { ArrowRight, CalendarDays, CalendarPlus, CalendarX2, ChevronDown, ChevronUp } from "lucide-react";
import { useRef, useState } from "react";
import { copy } from "@/copy";
import type { Breakpoint } from "@/domain/breakpoint";
import { eventStatus } from "@/domain/eventStatus";
import type { CalendarEvent, Tag } from "@/domain/schemas";
import { filterScope, matchesFilter, type TagFilter } from "@/domain/tags";
import { type Clock, clockAt } from "@/domain/time";
import { earlierFold, scheduleCount } from "@/domain/todaySchedule";
import type { ZoneState } from "@/domain/zoneState";
import { DayList } from "@/features/calendar";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import { AllDayTiles } from "@/ui/AllDayTile";
import { Button } from "@/ui/Button";
import { DayTimeline, type DayTimelineLabels, type TimelineWeather } from "@/ui/DayTimeline";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { IconButton } from "@/ui/IconButton";
import { press } from "@/ui/press";
import { Skeleton, SkeletonBlock } from "@/ui/Skeleton";
import { SourceFailure } from "@/ui/SourceFailure";
import { useBoxSize } from "@/ui/useBoxSize";
import { WhoFilter } from "@/ui/WhoFilter";
import { Zone } from "@/ui/Zone";
import { ComingUp } from "./ComingUp";
type Slot = {
  start: Date;
  end: Date;
};
type TodayScheduleProps = {
  state: ZoneState<CalendarEvent>;
  tags: readonly Tag[];
  selected: ReadonlySet<string>;
  onFilter: (filter: TagFilter) => void;
  retryIn: number;
  onRetry: () => void;
  day: string;
  now: Date;
  clock: Clock;
  tz: string;
  breakpoint: Breakpoint;
  iconActions: boolean;
  weather?: TimelineWeather;
  upcoming: readonly CalendarEvent[] | undefined;
  onOpenEvent: (event: CalendarEvent) => void;
  onAddEvent: () => void;
  onAddSlot: (slot: Slot) => void;
  onOpenCalendar: () => void;
  xstyle?: stylex.StyleXStyles;
};
type HeaderButtons = "text" | "touch" | "small";
type ActionsProps = {
  buttons: HeaderButtons;
  canAdd: boolean;
  onAddEvent: () => void;
  onOpenCalendar: () => void;
};
type ScheduleProps = Pick<TodayScheduleProps, "tags" | "selected" | "day" | "now" | "clock" | "tz" | "breakpoint" | "weather" | "onOpenEvent" | "onAddSlot" | "onOpenCalendar"> & {
  events: readonly CalendarEvent[];
  nowDot: boolean;
  showEarlier: boolean;
  onToggleEarlier: () => void;
};
const LABELS: DayTimelineLabels = {
  ...copy.timeline,
  everyone: copy.events.everyone,
  icons: copy.weather.icon
};
const SKELETON_ROWS = [0, 1, 2, 3, 4];
const NARROW_CARD = 450;
const TINY_CARD = 400;
const STACK_MAX = {
  wide: 5,
  narrow: 3
} as const;
const styles = {
  root: {
    kWkggS: "xh540kj",
    kaIpWk: "xwe7p7i",
    kLKAdn: "xu2p5k7",
    kg3NbH: "xw3ndq7",
    kGO01o: "x2xw44r",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x152posx",
    k7Eaqz: "xeuugli",
    kAzted: "x2lwn1j",
    $$css: true
  },
  touchTop: {
    kLKAdn: "x1q88djf",
    $$css: true
  },
  smallButton: {
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    $$css: true
  },
  earlier: {
    kSGwAc: "xqcrz7y",
    kAzted: "x1gg8mnh",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "x5r8ks9",
    kMwMTN: "x1a11ngh",
    k8WAf4: "xt970qd",
    kZCmMZ: "x1onr9mi",
    kwRFfy: "x1x5flf6",
    keTefX: "x1ybojm6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x152posx",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1j61zf2",
    k63SB2: "x1cf7cre",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  skeleton: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1ur044a",
    k8WAf4: "x1xckggx",
    kg3NbH: "xw3ndq7",
    $$css: true
  },
  failure: {
    k8WAf4: "x1xckggx",
    kg3NbH: "x96kwfj",
    $$css: true
  },
  timeline: {
    kAzted: "xz65tgg",
    $$css: true
  }
};
export function TodaySchedule({
  state,
  retryIn,
  onRetry,
  iconActions,
  upcoming,
  onFilter,
  onAddEvent,
  xstyle,
  ...schedule
}: TodayScheduleProps) {
  const ref = useRef<HTMLElement>(null);
  const [showEarlier, setShowEarlier] = useState(false);
  const width = useBoxSize(ref)?.width ?? Number.POSITIVE_INFINITY;
  const narrow = width < NARROW_CARD;
  const buttons = headerButtons(narrow, iconActions);
  const shown = shownBy(schedule.selected, schedule.tags);
  const ahead = upcoming && shown(upcoming);
  const scheduleOf = (events: readonly CalendarEvent[]) => <Schedule events={events} nowDot={width < TINY_CARD} showEarlier={showEarlier} onToggleEarlier={() => setShowEarlier(open => !open)} {...schedule} />;
  const comingUp = ahead && ahead.length > 0 && <ComingUp events={ahead} tags={schedule.tags} now={schedule.now} clock={schedule.clock} tz={schedule.tz} small={schedule.breakpoint === "small"} onOpenEvent={schedule.onOpenEvent} />;
  return <section ref={ref} {...stylex.props(styles.root, buttons === "touch" && styles.touchTop, xstyle)}>
      <div {...{
      0: {
        className: "x78zum5 x1a02dak x1qughib x6s0dn4 x152posx xjeie6f x1qj0mhr"
      },
      1: {
        className: "x78zum5 x1qughib x6s0dn4 x152posx xjeie6f x1qj0mhr xozqiw3"
      }
    }[!!narrow << 0]}>
        <Eyebrow>{eyebrowFor(state, shown, schedule, narrow)}</Eyebrow>
        <div className="x78zum5 xt4j9g6">
          <Actions buttons={buttons} canAdd={state.kind === "ready" || state.kind === "empty"} onAddEvent={onAddEvent} onOpenCalendar={schedule.onOpenCalendar} />
        </div>
      </div>
      {schedule.tags.length > 1 && <WhoFilter tags={schedule.tags} selected={schedule.selected} size="today" max={narrow ? STACK_MAX.narrow : STACK_MAX.wide} labels={copy.personFilter} onChange={onFilter} />}
      <Zone state={state} loading={<ScheduleSkeleton />} empty={comingUp || ahead && scheduleOf([])} failed={<SourceFailure icon={CalendarX2} title={copy.today.unavailable.calendar} body={copy.zone.failedBody(retryIn)} retryLabel={copy.zone.retry} onRetry={onRetry} xstyle={styles.failure} />}>
        {events => scheduleOf(shown(events))}
      </Zone>
    </section>;
}
function headerButtons(narrow: boolean, iconActions: boolean): HeaderButtons {
  if (narrow) return "small";
  return iconActions ? "touch" : "text";
}
function Actions({
  buttons,
  canAdd,
  onAddEvent,
  onOpenCalendar
}: ActionsProps) {
  if (buttons === "text") {
    return <>
        {canAdd && <Button variant="ghost" icon={CalendarPlus} onClick={onAddEvent}>
            {copy.today.schedule.addEvent}
          </Button>}
        <Button variant="ghost" iconRight={ArrowRight} onClick={onOpenCalendar}>
          {copy.today.schedule.fullCalendar}
        </Button>
      </>;
  }
  const size = buttons === "touch" ? "touch" : "md";
  const xstyle = buttons === "small" ? styles.smallButton : undefined;
  return <>
      {canAdd && <IconButton icon={CalendarPlus} label={copy.today.schedule.addEvent} variant="canvas" size={size} onClick={onAddEvent} xstyle={xstyle} />}
      <IconButton icon={CalendarDays} label={copy.today.schedule.fullCalendar} variant="canvas" size={size} onClick={onOpenCalendar} xstyle={xstyle} />
    </>;
}
function shownBy(selected: ReadonlySet<string>, tags: readonly Tag[]) {
  const allIds = tags.map(tag => tag.id);
  return (events: readonly CalendarEvent[]) => events.filter(event => matchesFilter(event, selected, allIds));
}
function eyebrowFor(state: ZoneState<CalendarEvent>, shown: (events: readonly CalendarEvent[]) => readonly CalendarEvent[], {
  now,
  clock,
  tz
}: Pick<TodayScheduleProps, "now" | "clock" | "tz">, short: boolean): string {
  if (state.kind === "loading") return copy.today.schedule.loadingCount;
  if (state.kind === "failed") return copy.today.schedule.failedCount;
  const events = state.kind === "ready" ? shown(state.data) : [];
  const count = copy.today.schedule.count(scheduleCount(events, now, tz), short);
  if (state.kind !== "ready" || state.updatedAt === undefined) return count;
  return copy.zone.updated(count, clockAt(state.updatedAt, clock, tz));
}
function ScheduleSkeleton() {
  return <Skeleton label={copy.today.schedule.loading} xstyle={styles.skeleton}>
      {SKELETON_ROWS.map(row => <SkeletonBlock key={row} height={44} radius={radii.md} />)}
    </Skeleton>;
}
function Schedule({
  events,
  tags,
  selected,
  day,
  now,
  clock,
  tz,
  breakpoint,
  weather,
  nowDot,
  showEarlier,
  onToggleEarlier,
  onOpenEvent,
  onAddSlot,
  onOpenCalendar
}: ScheduleProps) {
  const free = filterScope(selected, tags.map(tag => tag.id));
  if (breakpoint === "small") {
    return <DayList events={events} tags={tags} day={day} now={now} tz={tz} clock={clock} free={free} weather={weather} earlierOpen={showEarlier} onToggleEarlier={onToggleEarlier} onOpenEvent={onOpenEvent} onAddEvent={onAddSlot} />;
  }
  const timed = events.filter(event => !event.allDay);
  const fold = earlierFold(timed, now, tz, showEarlier);
  const plans = !showEarlier ? timed.filter(event => eventStatus(event, now, tz) !== "past") : timed;
  return <>
      <AllDayTiles events={events.filter(event => event.allDay)} tags={tags} label={copy.events.allDay} everyoneLabel={copy.events.everyone} onOpenEvent={onOpenEvent} />
      {fold.done > 0 && <button type="button" aria-expanded={showEarlier} onClick={onToggleEarlier} {...stylex.props(styles.earlier, press.scale)}>
          <Icon icon={showEarlier ? ChevronUp : ChevronDown} size={18} strokeWidth={2} />
          {showEarlier ? copy.today.schedule.hideEarlier : copy.today.schedule.earlier(fold.done)}
        </button>}
      <DayTimeline plans={plans} tags={tags} day={day} now={now} tz={tz} clock={clock} variant="today" breakpoint={breakpoint} free={free} weather={weather} firstHour={fold.fromHour} lastHour={fold.toHour} grow={breakpoint !== "large"} nowDot={nowDot} labels={LABELS} onOpenEvent={onOpenEvent} onOpenMore={onOpenCalendar} onAddEvent={onAddSlot} xstyle={styles.timeline} />
    </>;
}